import type React from "react";
import { BrowserRouter as Router, Routes, Route, Navigate } from "react-router-dom";
import LoginPage from "./pages/auth/Login";
import SignupPage from "./pages/auth/Signup";
import ForgotPasswordPage from "./pages/auth/ForgotPassword";
import ResetPasswordPage from "./pages/auth/ResetPasswordPage";
import CourseList from "./pages/courses/CourseList";
import CreateCourse from "./pages/courses/CreateCourse";
import EditCourse from "./pages/courses/EditCourse";
import CourseDetails from "./pages/courses/CourseDetails";
import ProtectedRoute from "./components/common/ProtectedRoute";
import PackageList from "./pages/packages/PackageList";
import UserDashboard from "./pages/user/UserDashboard";
import EnhancedUserDashboard from "./pages/user/EnhancedUserDashboard";
import UserOfferings from "./pages/user/UserOfferings";
import EnhancedUserOfferings from "./pages/user/EnhancedUserOfferings";
import UserPurchases from "./pages/user/UserPurchases";
import VideoPlayerPage from "./pages/user/VideoPlayer";
import CreatePackage from "./pages/packages/CreatePackage";
import PackageDetails from "./pages/packages/PackageDetails";

import CourseLandingPage from "./pages/courses/CourseLandingPage";
import PackageLandingPage from "./pages/packages/PackageLandingPage";
import EditPackage from "./pages/packages/EditPackage";
import RolesPage from "./pages/roles/RolesPage";

import UserManagementDashboard from "./pages/admin/UserManagementDashboard";
import EnhancedAdminDashboard from "./pages/admin/EnhancedAdminDashboard";
import Profile from "./pages/Profile";
import CompaniesList from "./components/common/CompaniesList";
import DepartmentsList from "./components/common/DepartmentsList";

import NotFoundPage from "./pages/NotFound";
import "primereact/resources/themes/lara-light-indigo/theme.css";
import "primereact/resources/primereact.min.css";
import "primeicons/primeicons.css";
import AdminLearnRKBPage from "./pages/admin/learnrkb";
import UserLearnRKBPage from "./pages/user/learnrkb";
import UserlistData from "../src/pages/admin/UserlistData"
import DashboardLayout from "./components/layout/DashboardLayout";
import PdfReportViewer from "./pages/learnrkb/pdfReportViewer";
import LearnUserList from "../../RKBLMSFRONTEND/src/pages/admin/Userlist";
import LearnSubscriberUser from "../src/pages/UserList/SubscriberUser"
import UserManagementWrapper from "../src/pages/admin/UserManagementWrapper"
import AdminWatchHistory from "./pages/admin/AdminWatchHistory"
import { AuthProvider } from "./context/AuthContext"

const PublicOnlyRoute: React.FC<{ element: React.ReactElement }> = ({ element }) => {
  const token = localStorage.getItem('AUTH_TOKEN');
  const userData = localStorage.getItem('userData');
  if (token) {
    try {
      const user = userData ? JSON.parse(userData) : null;
      const isAdmin = user?.isAdmin;
      return <Navigate to={isAdmin ? '/enhanced-admin-dashboard' : '/user-dashboard'} replace />;
    } catch {
      return <Navigate to="/user-dashboard" replace />;
    }
  }
  return element;
};

const App: React.FC = () => {
  return (
    <AuthProvider>
      <Router>
        <Routes>
          <Route path="/" element={<PublicOnlyRoute element={<LoginPage />} />} />
          <Route path="/login" element={<PublicOnlyRoute element={<LoginPage />} />} />
          <Route path="/signup" element={<SignupPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />

          {/* Protected Routes — any logged-in user */}
          <Route element={<ProtectedRoute />}>
            <Route path="/dashboard" element={<Navigate to="/enhanced-admin-dashboard" replace />} />

            <Route path="/profile" element={<Profile />} />
            <Route path="/user-dashboard" element={<EnhancedUserDashboard />} />
            <Route path="/user-offerings" element={<EnhancedUserOfferings />} />
            {/* <Route path="/user-list" element={<UserListPage />} /> */}
            <Route path="/courses" element={<CourseList />} />
            <Route path="/courses/landing/:id" element={<CourseLandingPage />} />
            <Route path="/courses/:id" element={<CourseDetails />} />
            <Route path="/packages/landing/:id" element={<PackageLandingPage />} />
            <Route path="/packages/:id" element={<PackageDetails />} />
            <Route path="/package" element={<PackageList />} />

            <Route path="/UserDashboard" element={<UserDashboard />} />
            <Route path="/UserOfferings" element={<UserOfferings />} />
            <Route path="/UserPurchases" element={<UserPurchases />} />

            {/* User */}
            <Route path="/user/learnrkb" element={<UserLearnRKBPage />} />

            <Route path="/pdfReportView" element={<PdfReportViewer />} />

            <Route path="/video/:courseId" element={<VideoPlayerPage />} />

            {/* Watch History — admin, moderator and support_user all get this
                one, unlike the rest of the admin-only surface below. */}
            <Route element={<ProtectedRoute allowedRoles={["admin", "moderator", "support_user"]} />}>
              <Route path="/admin/watch-history" element={<AdminWatchHistory />} />
            </Route>

            {/* Admin-only — role is re-checked live (see AuthContext), so a
                role change made directly in the DB takes effect within
                ROLE_REFRESH_INTERVAL_MS without the user logging out. */}
            <Route element={<ProtectedRoute allowedRoles={["admin"]} />}>
              <Route path="/user-management-dashboard" element={<UserManagementDashboard />} />
              <Route path="/user-management" element={<DashboardLayout><UserManagementWrapper /></DashboardLayout>} />
              <Route path="/companies" element={<DashboardLayout><CompaniesList /></DashboardLayout>} />
              <Route path="/departments" element={<DashboardLayout><DepartmentsList /></DashboardLayout>} />
              <Route path="/add-user" element={<DashboardLayout><UserManagementWrapper /></DashboardLayout>} />
              <Route path="/enhanced-admin-dashboard" element={<EnhancedAdminDashboard />} />
              <Route path="/courses/create" element={<CreateCourse />} />
              <Route path="/courses/edit/:id" element={<EditCourse />} />
              <Route path="/packages/create" element={<CreatePackage />} />
              <Route path="/packages/edit/:id" element={<EditPackage />} />
              <Route path="/roles" element={<RolesPage />} />

              <Route path="/admin/learnrkb" element={<AdminLearnRKBPage />} />
              <Route path="/admin/userlist" element={<DashboardLayout> <LearnUserList /> </DashboardLayout>} />
              {/* <Route path="/admin/userlist/data" element={ <DashboardLayout> <UserlistData /></DashboardLayout>}/>          */}
              <Route path="/admin/userlist/data" element={<UserlistData />} />
              <Route path="/importUserExcel" element={<LearnSubscriberUser />} />
            </Route>
          </Route>

          <Route path="/home" element={<Navigate to="/dashboard" replace />} />

          {/* Catch-all route for 404 */}
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </Router>
    </AuthProvider>
  );
};

export default App;